/**
 * Which scenes, products and camera styles belong together.
 *
 * `config_links` records, per scene, the product types it is written for and
 * the camera styles it supports. The start dialog uses it to narrow each
 * choice to the next; this module exists so the composer can use the same rows
 * to notice a pairing that stopped making sense — which happens as soon as a
 * product is changed after the scene was chosen.
 *
 * A scene with no links of a kind is universal for that kind. Links constrain
 * where they exist; they are not a whitelist everything must join, or a newly
 * created scene would appear compatible with nothing.
 */

export interface ConfigLink {
  brief_config_id: string;
  linked_config_id: string;
  linked_type: string;
}

function linkedIds(links: ConfigLink[], sceneId: string, type: string): Set<string> {
  return new Set(
    links.filter((l) => l.brief_config_id === sceneId && l.linked_type === type)
      .map((l) => l.linked_config_id),
  );
}

/** Scenes written for this product type — or all of them, if none say. */
export function scenesForProductType<T extends { id: string }>(
  scenes: T[], links: ConfigLink[], productConfigId?: string | null,
): T[] {
  if (!productConfigId || links.length === 0) return scenes;
  const constrained = new Set(
    links.filter((l) => l.linked_type === "product").map((l) => l.brief_config_id),
  );
  const forThisType = new Set(
    links.filter((l) => l.linked_type === "product" && l.linked_config_id === productConfigId)
      .map((l) => l.brief_config_id),
  );
  return scenes.filter((s) => forThisType.has(s.id) || !constrained.has(s.id));
}

/** Camera styles this scene supports — or all of them, if none are listed. */
export function camerasForScene<T extends { id: string }>(
  cameras: T[], links: ConfigLink[], sceneId?: string | null,
): T[] {
  if (!sceneId || links.length === 0) return cameras;
  const allowed = linkedIds(links, sceneId, "photographer");
  if (allowed.size === 0) return cameras;
  return cameras.filter((c) => allowed.has(c.id));
}

export type Mismatch =
  | { kind: "product"; }
  | { kind: "camera"; };

/**
 * What no longer fits, given a scene and the current choices.
 *
 * Returns only genuine conflicts: a product whose type is linked to other
 * scenes but not this one, and a camera excluded from a scene that names its
 * supported styles. Silence when a scene says nothing about a kind.
 */
export function findMismatches(args: {
  links: ConfigLink[];
  sceneId?: string | null;
  productConfigId?: string | null;
  cameraId?: string | null;
}): Mismatch[] {
  const { links, sceneId, productConfigId, cameraId } = args;
  if (!sceneId || links.length === 0) return [];
  const out: Mismatch[] = [];

  if (productConfigId) {
    const productTypes = linkedIds(links, sceneId, "product");
    // Only a conflict if this scene names its product types at all.
    if (productTypes.size > 0 && !productTypes.has(productConfigId)) {
      out.push({ kind: "product" });
    }
  }

  if (cameraId) {
    const allowed = linkedIds(links, sceneId, "photographer");
    if (allowed.size > 0 && !allowed.has(cameraId)) {
      out.push({ kind: "camera" });
    }
  }

  return out;
}
